import { useMemo, useCallback } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Button } from 'antd'
import { HStack, Icon, List, Table, Text, Tooltip, VStack } from 'govwell-ui'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import { ScrapeTemplateFragment, ScrapeFragment } from 'types/graphql'

import Tag from 'src/components/Tag'
import { useScrapeTemplatesQuery } from 'src/fetch/scrapeTemplates'
import useDisclosure from 'src/hooks/use-disclosure'
import { DateFormats, formatDate } from 'src/utils/date'

import ScrapeTemplateModal from './ScrapeTemplateModal'
import ScrapeTemplatesTableButtons from './ScrapeTemplatesTableButtons'

const StatusTag = ({ scrape }: { scrape: ScrapeFragment | null | undefined }) => {
  if (!scrape) {
    return null
  }
  switch (scrape.status) {
    case 'Completed':
      return <Tag color="green">Completed</Tag>
    case 'InProgress':
      return <Tag color="yellow">In Progress</Tag>
    case 'Failed':
      return (
        <Tooltip content={scrape.failedReason} asChild={false}>
          <Tag color="red">Failed</Tag>
        </Tooltip>
      )
    default:
      return null
  }
}

const ScrapeTemplatesTable = () => {
  const tableProps = useTableState<ScrapeTemplateFragment>()
  const { scrapeTemplates, isFetching, refetch } = useScrapeTemplatesQuery()
  const createModal = useDisclosure()

  const getRowId = useCallback((d: ScrapeTemplateFragment) => `${d.id}`, [])

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<ScrapeTemplateFragment>()
    return [
      columnHelper.accessor('startUrl', {
        id: 'startUrl',
        header: 'Website URL',
        cell: (info) => info.getValue(),
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'status',
        header: 'Status',
        cell: ({ row: { original: scrapeTemplate } }) => (
          <HStack gap={2}>
            <StatusTag scrape={scrapeTemplate.mostRecentScrape} />
          </HStack>
        ),
      }),
      columnHelper.accessor(
        (scrapeTemplate) =>
          scrapeTemplate.mostRecentScrape?.startedAt
            ? formatDate(scrapeTemplate.mostRecentScrape.startedAt, DateFormats.NumericalDateTime)
            : '-',
        {
          id: 'startedAt',
          header: 'Started',
          cell: (info) => info.getValue(),
        }
      ),
      columnHelper.accessor(
        (scrapeTemplate) => {
          const endedAt =
            scrapeTemplate.mostRecentScrape?.completedAt ||
            scrapeTemplate.mostRecentScrape?.failedAt
          return endedAt ? formatDate(endedAt, DateFormats.NumericalDateTime) : ''
        },
        {
          id: 'endedAt',
          header: 'Ended',
          cell: (info) => info.getValue(),
        }
      ),
      columnHelper.display({
        id: 'trained',
        header: 'Trained',
        cell: ({ row: { original: scrapeTemplate } }) => {
          const progress = scrapeTemplate.mostRecentScrape?.progress
          const pages = progress?.pagesAITrained || 0
          const documents = progress?.documentsCount || 0
          return (
            <VStack gap={0}>
              <Tooltip content="Web pages downloaded and used to train the AI Community Assistant">
                <Text.Body as="span">
                  {pages} {pages === 1 ? 'page' : 'pages'}
                </Text.Body>
              </Tooltip>
              {documents > 0 && (
                <Tooltip content="Documents (PDFs, Word, PowerPoint, etc.) found and used to train the AI Community Assistant">
                  <Text.BodySmall as="span" className="text-gray-500">
                    {documents} {documents === 1 ? 'document' : 'documents'}
                  </Text.BodySmall>
                </Tooltip>
              )}
            </VStack>
          )
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        meta: { textAlign: 'right' },
        cell: ({ row: { original: scrapeTemplate } }) => (
          <ScrapeTemplatesTableButtons scrapeTemplate={scrapeTemplate} refetch={refetch} />
        ),
      }),
    ]
  }, [refetch])

  return (
    <>
      <HStack justify="space-between">
        <Text.Body>
          For each website you add, the Community Assistant will:
          <List>
            <List.Item>Securely scan your public pages</List.Item>
            <List.Item>Collect key information from the site&apos;s content</List.Item>
            <List.Item>Use that information to provide accurate answers to questions</List.Item>
          </List>
        </Text.Body>
        <Button icon={<Icon.FaPlus />} type="primary" onClick={createModal.open}>
          Add website
        </Button>
      </HStack>
      <Table
        columns={columns}
        data={scrapeTemplates}
        getRowId={getRowId}
        isLoading={isFetching}
        emptyStateMessage="No external websites added yet"
        shouldHideEmptyStateImage
        {...tableProps}
      />
      {createModal.isOpen && <ScrapeTemplateModal onClose={createModal.close} refetch={refetch} />}
    </>
  )
}

export default ScrapeTemplatesTable
